import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { GrantForm } from './GrantForm';
import { expiresAtToIso, resolveGrant, type GrantFormState } from './grant-state';
import { EMPTY_SCOPE_SELECTION, scopeSelectionGap } from './scope-selection';
import { previewGrant, type HierarchySnapshot } from './effective-permissions';
import { createRoleAssignment, listRoles } from '@/lib/api/endpoints/technicians';
import { listDeviceGroups, listOrganizations, listSites } from '@/lib/api/endpoints/organizations';
import { listDevices } from '@/lib/api/endpoints/devices';
import { queryKeys } from '@/lib/query-keys';
import { mergeFieldErrors, type LocalFieldErrors } from '@/lib/form-errors';
import type { RoleAssignment, Technician } from '@/lib/api/types';

/*
  Add a role assignment to an existing technician.

  The same decision as the invite dialog's second half, reusing the same GrantForm and the same
  no-escalation preview, so the two paths cannot drift into disagreeing about what a grant means.

  Assignments accumulate rather than replace. That is the model, not a shortcut: "Technician on
  Acme Dental" plus "Technician on Beta Legal / Records Annex" is two grants, and collapsing them
  into one editable row would have to invent a way to express a set of scopes, which the schema
  deliberately does not have.
*/

const SCOPE_DEVICE_LIMIT = 500;

export interface AssignRoleDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly technician: Technician;
  readonly grantorAssignments: readonly RoleAssignment[];
}

export function AssignRoleDialog({
  open,
  onOpenChange,
  technician,
  grantorAssignments,
}: AssignRoleDialogProps): React.JSX.Element {
  const formId = `assign-${useId()}`;
  const queryClient = useQueryClient();

  const [grant, setGrant] = useState<GrantFormState>({
    roleId: null,
    scope: EMPTY_SCOPE_SELECTION,
    expiresAtLocal: '',
  });
  const [local, setLocal] = useState<LocalFieldErrors>({});

  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setGrant({ roleId: null, scope: EMPTY_SCOPE_SELECTION, expiresAtLocal: '' });
      setLocal({});
    }
  }

  const roles = useQuery({ queryKey: queryKeys.roles, queryFn: listRoles });
  const organizations = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: listOrganizations,
  });
  const sites = useQuery({ queryKey: queryKeys.sites(), queryFn: () => listSites() });
  const groups = useQuery({
    queryKey: queryKeys.deviceGroups(),
    queryFn: () => listDeviceGroups(),
  });
  const devices = useQuery({
    queryKey: queryKeys.devices({ limit: SCOPE_DEVICE_LIMIT }),
    queryFn: () => listDevices({ limit: SCOPE_DEVICE_LIMIT }),
  });

  const hierarchy: HierarchySnapshot = useMemo(
    () => ({
      sites: sites.data ?? [],
      groups: groups.data ?? [],
      devices: devices.data?.items ?? [],
    }),
    [sites.data, groups.data, devices.data],
  );

  const allRoles = useMemo(() => roles.data ?? [], [roles.data]);
  const loading =
    roles.isPending || organizations.isPending || sites.isPending || groups.isPending || devices.isPending;

  const mutation = useMutation({
    mutationFn: async (): Promise<RoleAssignment> => {
      const resolved = resolveGrant(grant, allRoles);
      if (resolved === null) {
        throw new Error('The role and scope are not complete.');
      }
      return createRoleAssignment({
        userId: technician.id,
        roleId: resolved.role.id,
        scope: resolved.scope,
        expiresAt: expiresAtToIso(grant.expiresAtLocal),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.technicians });
      void queryClient.invalidateQueries({ queryKey: queryKeys.auditEvents('') });
      onOpenChange(false);
    },
  });

  const errors = mergeFieldErrors(local, mutation.error);
  const resolved = resolveGrant(grant, allRoles);
  const preview =
    resolved === null
      ? null
      : previewGrant(resolved.role, resolved.scope, grantorAssignments, allRoles, hierarchy);
  const escalation = preview !== null && preview.blocked.length > 0;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (grant.roleId === null) {
      found.roleId = 'Choose a role.';
    }
    const gap = scopeSelectionGap(grant.scope);
    if (gap !== null) {
      found.scope = gap;
    }
    const iso = expiresAtToIso(grant.expiresAtLocal);
    if (grant.expiresAtLocal !== '' && iso === null) {
      found.expiresAt = 'That is not a valid date and time.';
    } else if (iso !== null && Date.parse(iso) <= Date.now()) {
      found.expiresAt = 'An expiry in the past would grant nothing.';
    }
    setLocal(found);
    if (Object.keys(found).length > 0 || escalation) {
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Grant a role to ${technician.fullName}`}
      description={`${technician.email}. Assignments add up: this does not replace the ${String(technician.assignments.length)} they already hold.`}
      width="lg"
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={mutation.isPending}
            disabled={escalation || loading}
            title={
              escalation
                ? 'This grant includes permissions you do not hold at that scope. See the list above.'
                : undefined
            }
          >
            Grant role
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <GrantForm
          state={grant}
          onChange={setGrant}
          subject={technician.fullName}
          roles={allRoles}
          organizations={organizations.data ?? []}
          hierarchy={hierarchy}
          grantorAssignments={grantorAssignments}
          loading={loading}
          {...(errors.forField('roleId') === undefined
            ? {}
            : { roleError: errors.forField('roleId') })}
          {...(errors.forField('scope') === undefined
            ? {}
            : { scopeError: errors.forField('scope') })}
          {...(errors.forField('expiresAt') === undefined
            ? {}
            : { expiresAtError: errors.forField('expiresAt') })}
        />

        {mutation.isError && !errors.allErrorsAreFieldLevel ? (
          <ApiErrorAlert error={mutation.error} context="Could not grant the role" />
        ) : null}
      </form>
    </Dialog>
  );
}
